import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { HStack } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import PINSAIIconButton from '../../../../../../../../components/PINSAIIconButton';
import { CypressIds } from '../../../../../../../../constants/cypressIds';
import { FailedGptRequestStatuses } from '../../../../../../../../constants/gptRequestStatuses';
import { showModal } from '../../../../../../../../redux/modules/modal';
import { useRecordGptRequests, useRecordQuestionRequirements } from '../../../../../../../../services/record-service';
import { getInfiniteListData } from '../../../../../../../../services/util';

import QuestionRequirementModal from './QuestionRequirementModal';

const QuestionRequirementIconButton = ({
  recordId,
  resourceType,
  resourceId,
  resourceDisplayName,
  policyId = undefined,
  ...props
}) => {
  const dispatch = useDispatch();
  const { data: questions, isLoading: questionsLoading } = useRecordQuestionRequirements(recordId);
  const { data: gptRequests, isLoading: gptRequestsLoading } = useRecordGptRequests({ recordId });

  const relatedQuestions = useMemo(() => {
    if (questionsLoading) {
      return [];
    }

    return questions.filter(question => question.links.find(link => link.resourceType === resourceType && link.resourceId === resourceId));
  }, [questions, questionsLoading]);

  const isUnread = useMemo(() => relatedQuestions.find(q => {
    if (!q.answers.length) {
      return false;
    }

    const mostRecentAnswer = q.answers[q.answers.length - 1];

    return !!mostRecentAnswer.publishedAt && !mostRecentAnswer.viewedAt;
  }), [relatedQuestions]);

  const hasError = useMemo(() => {
    if (gptRequestsLoading) {
      return false;
    }

    const data = getInfiniteListData(gptRequests);

    if (!data.length) {
      return false;
    }

    return FailedGptRequestStatuses.includes(data[0]?.status);
  }, [gptRequests, gptRequestsLoading]);

  const cypressId = useMemo(() => {
    if (resourceType === 'insurance_policy_type') {
      return CypressIds.policyQuestionRequirementIconButton(resourceId, policyId);
    }

    if (resourceType === 'insurance_document_type') {
      return CypressIds.docQuestionRequirementIconButton(resourceId);
    }

    return undefined;
  }, [resourceType, resourceId]);

  return (
    <HStack
      alignItems="flex-start"
      spacing={0}
      onClick={() => {
        dispatch(showModal(
          QuestionRequirementModal,
          { size: '7xl' },
          { recordId, questions, resourceDisplayName, resourceType, resourceId }
        ));
      }}
      {...props}
    >
      <PINSAIIconButton
        aria-label="pins-ai-icon"
        data-cy={cypressId}
        data-error={hasError}
        data-unread={!!isUnread}
        error={hasError}
        notification={!!isUnread}
      />
    </HStack>
  );
};

QuestionRequirementIconButton.propTypes = {
  recordId: PropTypes.number.isRequired,
  resourceType: PropTypes.string.isRequired,
  resourceId: PropTypes.number.isRequired,
  resourceDisplayName: PropTypes.string.isRequired,
  policyId: PropTypes.number,
  docId: PropTypes.number
};

export default QuestionRequirementIconButton;
